﻿import { FormEvent, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowUp, Boxes, Eye, GripVertical, ImagePlus, Info, Plus, Save, Sparkles, Star, Store, Trash2, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, getCurrentAdmin, logout } from "./api";
import {
  Product,
  ProductCategory,
  ProductPayload,
  ProductType,
  ProductUsageType,
  ProductVariant,
  deleteAdminProductImage,
  getAdminProduct,
  listAdminProductCategories,
  saveAdminProduct,
  uploadAdminProductImage,
} from "@/services/products";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";
import { AdminFormNotice } from "./components/AdminFormNotice";
import { AdminRichTextEditor } from "./components/AdminRichTextEditor";
import ProductGallery from "../product-detail/components/ProductGallery";
import ProductFeatures from "../product-detail/components/ProductFeatures";
import ProductSpecs from "../product-detail/components/ProductSpecs";

type VariantAttributeForm = {
  Name: string;
  Values: string[];
};

type VariantRowForm = {
  Sku: string;
  OptionValues: Record<string, string>;
  Price: string;
  SalePrice: string;
  Stock: string;
  ImageUrl: string;
  IsDefault: boolean;
  IsActive: boolean;
};

type ProductFormState = {
  Name: string;
  Slug: string;
  Subtitle: string;
  ProductCode: string;
  Barcode: string;
  Category: string;
  ParentCategoryId: string;
  ChildCategoryId: string;
  ProductType: ProductType;
  ProductUsageType: ProductUsageType;
  AllowZeroPriceOrder: boolean;
  HasVariants: boolean;
  Price: string;
  SalePrice: string;
  Stock: string;
  TagsText: string;
  ShortDescription: string;
  ProductImageUrls: string[];
  Description: string;
  FeatureItemsText: string;
  SpecItemsText: string;
  QuickInfoItemsText: string;
  VariantAttributes: VariantAttributeForm[];
  Variants: VariantRowForm[];
  IsFeatured: boolean;
  IsNew: boolean;
  IsHot: boolean;
  IsPublished: boolean;
  SortOrder: string;
};

const emptyForm: ProductFormState = {
  Name: "",
  Slug: "",
  Subtitle: "",
  ProductCode: "",
  Barcode: "",
  Category: "",
  ParentCategoryId: "",
  ChildCategoryId: "",
  ProductType: "Sale",
  ProductUsageType: "Sale",
  AllowZeroPriceOrder: false,
  HasVariants: false,
  Price: "",
  SalePrice: "",
  Stock: "",
  TagsText: "",
  ShortDescription: "",
  ProductImageUrls: [],
  Description: "",
  FeatureItemsText: "",
  SpecItemsText: "",
  QuickInfoItemsText: "",
  VariantAttributes: [],
  Variants: [],
  IsFeatured: false,
  IsNew: false,
  IsHot: false,
  IsPublished: true,
  SortOrder: "100",
};

const productUsageOptions: { value: ProductUsageType; label: string; description: string }[] = [
  { value: "Sale", label: "一般銷售", description: "正常販售商品，門市訂單成交價不建議為 0。" },
  { value: "Trial", label: "試戴/借出", description: "提供客戶試戴、借出或展示服務，可搭配 0 元原因稽核。" },
  { value: "WarrantyReplacement", label: "保固更換", description: "保固內更換、維修換件或原廠處理。" },
  { value: "Gift", label: "贈品/活動", description: "活動贈送、搭配銷售贈品或專案服務。" },
  { value: "ServicePart", label: "補件/服務", description: "客訴、服務補件、配件補發或門市服務用途。" },
  { value: "Consumable", label: "耗材", description: "電池、清潔用品或其他消耗品，通常仍需收款。" },
  { value: "Test", label: "測試品", description: "測試資料或內部驗證用途，正式資料建議少用。" },
];

function defaultAllowZeroForUsage(value: ProductUsageType) {
  return value === "Trial" || value === "WarrantyReplacement" || value === "Gift" || value === "ServicePart" || value === "Test";
}

function buildOptionLabel(optionValues: Record<string, string>, attributes: VariantAttributeForm[]) {
  return attributes
    .map((attribute) => optionValues[attribute.Name])
    .filter((value): value is string => Boolean(value))
    .join(" / ");
}

/** 依目前規格屬性計算所有組合（笛卡兒積），僅使用有名稱且至少一個選項值的屬性。 */
function cartesianCombinations(attributes: VariantAttributeForm[]): Record<string, string>[] {
  const validAttributes = attributes
    .map((attribute) => ({ Name: attribute.Name.trim(), Values: attribute.Values.map((v) => v.trim()).filter(Boolean) }))
    .filter((attribute) => attribute.Name !== "" && attribute.Values.length > 0);

  if (validAttributes.length === 0) return [];

  return validAttributes.reduce<Record<string, string>[]>(
    (combinations, attribute) => {
      const next: Record<string, string>[] = [];
      combinations.forEach((combo) => {
        attribute.Values.forEach((value) => {
          next.push({ ...combo, [attribute.Name]: value });
        });
      });
      return next;
    },
    [{}]
  );
}

function comboKey(optionValues: Record<string, string>, attributeNames: string[]) {
  return attributeNames.map((name) => optionValues[name] ?? "").join("||");
}

function emptyVariantRow(optionValues: Record<string, string>, defaults: { Price: string; SalePrice: string }): VariantRowForm {
  return {
    Sku: "",
    OptionValues: optionValues,
    Price: defaults.Price || "0",
    SalePrice: defaults.SalePrice || "",
    Stock: "0",
    ImageUrl: "",
    IsDefault: false,
    IsActive: true,
  };
}

function moveVariantList<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= list.length) return list;
  const next = [...list];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

function splitLines(value: string) {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function productToForm(product: Product): ProductFormState {
  return {
    Name: product.name,
    Slug: product.slug || String(product.id),
    Subtitle: product.subtitle,
    ProductCode: product.productCode,
    Barcode: product.barcode || "",
    Category: product.category,
    ParentCategoryId: "",
    ChildCategoryId: "",
    ProductType: product.productType,
    ProductUsageType: product.productUsageType || "Sale",
    AllowZeroPriceOrder: product.allowZeroPriceOrder === true,
    HasVariants: product.hasVariants,
    Price: String(product.price || ""),
    SalePrice: product.salePrice ? String(product.salePrice) : "",
    Stock: product.stock !== null && product.stock !== undefined ? String(product.stock) : "",
    TagsText: product.tags.join("\n"),
    ShortDescription: product.shortDescription || "",
    ProductImageUrls: uniqueUrls([product.image, ...(product.galleryImages || [])]),
    Description: product.description,
    FeatureItemsText: (product.featureItems || []).map((item) => `${item.Title}｜${item.Description}`).join("\n"),
    SpecItemsText: (product.specItems || []).map((item) => `${item.Label}｜${item.Value}`).join("\n"),
    QuickInfoItemsText: (product.quickInfoItems || []).map((item) => `${item.Icon}｜${item.Label}｜${item.Value}`).join("\n"),
    VariantAttributes: (product.variantAttributes || []).map((attribute) => ({ Name: attribute.name, Values: [...attribute.values] })),
    Variants: (product.variants || []).map((variant) => ({
      Sku: variant.sku,
      OptionValues: { ...variant.optionValues },
      Price: String(variant.price || 0),
      SalePrice: variant.salePrice !== null ? String(variant.salePrice) : "",
      Stock: String(variant.stock || 0),
      ImageUrl: variant.imageUrl || "",
      IsDefault: variant.isDefault,
      IsActive: variant.isActive,
    })),
    IsFeatured: product.isFeatured === true,
    IsNew: product.isNew === true,
    IsHot: product.isHot === true,
    IsPublished: product.isPublished !== false,
    SortOrder: String(product.sortOrder || 100),
  };
}

function uniqueUrls(urls: string[]) {
  return Array.from(new Set(urls.map((url) => url.trim()).filter(Boolean)));
}

function splitPairLine(line: string) {
  const normalized = line.replace(/\|/g, "｜");
  const [first, ...rest] = normalized.split("｜");
  return {
    first: (first || "").trim(),
    second: rest.join("｜").trim(),
  };
}

function parseFeatureItems(value: string) {
  return value
    .split(/\r?\n/)
    .map(splitPairLine)
    .filter((item) => item.first || item.second)
    .map((item) => ({ Title: item.first, Description: item.second }));
}

function parseSpecItems(value: string) {
  return value
    .split(/\r?\n/)
    .map(splitPairLine)
    .filter((item) => item.first || item.second)
    .map((item) => ({ Label: item.first, Value: item.second }));
}

function parseQuickInfoItems(value: string) {
  return value
    .split(/\r?\n/)
    .map((line) => {
      const normalized = line.replace(/\|/g, "｜");
      const [icon, label, ...rest] = normalized.split("｜");
      return {
        Icon: (icon || "ri-information-line").trim(),
        Label: (label || "").trim(),
        Value: rest.join("｜").trim(),
      };
    })
    .filter((item) => item.Label || item.Value);
}

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/^-+|-+$/g, "");
}

function sanitizeEditorHtml(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.body.querySelectorAll("script, style").forEach((node) => node.remove());
  doc.body.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) node.removeAttribute(attr.name);
      if ((attr.name === "href" || attr.name === "src") && /^javascript:/i.test(attr.value.trim())) {
        node.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
}

function ProductDescriptionPreview({ description }: { description: string }) {
  if (!description.trim()) return null;
  return (
    <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-10">
      <h2 className="mb-5 text-xl font-bold text-[#1B3D2A] md:mb-6 md:text-2xl">產品詳細介紹</h2>
      <div
        className="max-w-none text-sm leading-7 text-[#5A6B5A] md:text-base [&_a]:font-bold [&_a]:text-[#1B3D2A] [&_blockquote]:border-l-4 [&_blockquote]:border-[#d7c7b6] [&_blockquote]:pl-4 [&_font[size='2']]:text-sm [&_font[size='4']]:text-lg [&_font[size='5']]:text-xl [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#1B3D2A] [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#1B3D2A] [&_iframe]:my-5 [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:rounded-xl [&_iframe]:border-0 [&_img]:my-5 [&_img]:max-h-[560px] [&_img]:w-full [&_img]:rounded-xl [&_img]:object-contain [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-4 [&_strong]:font-bold [&_strong]:text-[#1B3D2A] [&_ul]:list-disc"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </div>
  );
}

function VariantDefaultButton({ checked, onClick }: { checked: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-bold transition ${
        checked ? "bg-[#315746] text-white" : "border border-[#cfd8d2] bg-white text-[#5A6B5A] hover:border-[#315746] hover:text-[#315746]"
      }`}
    >
      <Star size={12} className={checked ? "fill-white" : ""} />
      {checked ? "預設組合" : "設為預設"}
    </button>
  );
}

function VariantActiveSwitch({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex h-8 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-bold text-[#315746]">
      <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${checked ? "bg-[#315746]" : "bg-[#c7cfca]"}`}>
        <input checked={checked} className="sr-only" type="checkbox" onChange={(event) => onChange(event.target.checked)} />
        <span
          className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full bg-white shadow-sm transition ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </span>
      {checked ? "已啟用" : "已停用"}
    </label>
  );
}

function PreviewCartSection({
  product,
  activeVariants,
  selectedVariant,
  onSelectVariantIndex,
}: {
  product: Product;
  activeVariants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  onSelectVariantIndex: (index: number) => void;
}) {
  if (product.productType !== "Sale") {
    return (
      <div className="mt-6 flex items-center gap-2 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] px-4 py-3 text-sm font-semibold text-[#9A8A7A]">
        <i className="ri-eye-line" />
        展示型商品：前台不會顯示加入購物車與數量選擇區塊。
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-xl border border-[#E8E0D6] p-5">
      <div className="flex items-center gap-2 text-xs font-bold text-[#1B3D2A]">
        <i className="ri-shopping-cart-2-line" />
        銷售型商品：前台會顯示以下區塊
      </div>

      {product.hasVariants && (
        <div className="mt-4 grid gap-3">
          {product.variantAttributes.map((attribute) => (
            <div key={attribute.name}>
              <div className="mb-1.5 text-xs font-semibold text-[#9A8A7A]">{attribute.name}</div>
              <div className="flex flex-wrap gap-2">
                {attribute.values.map((value) => {
                  const matchIndex = activeVariants.findIndex((variant) => variant.optionValues[attribute.name] === value);
                  const isSelected = selectedVariant?.optionValues[attribute.name] === value;
                  return (
                    <button
                      type="button"
                      key={value}
                      disabled={matchIndex === -1}
                      onClick={() => matchIndex !== -1 && onSelectVariantIndex(matchIndex)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        isSelected ? "border-[#1B3D2A] bg-[#1B3D2A] text-white" : "border-[#E8E0D6] text-[#5A6B5A] hover:border-[#1B3D2A]"
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-1">
          <span className="text-xs text-[#9A8A7A]">NT$</span>
          <span className="text-2xl font-bold text-[#1B3D2A]">
            {(selectedVariant ? (selectedVariant.salePrice ?? selectedVariant.price) : (product.salePrice ?? product.price)).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-[#E8E0D6]">
          <button type="button" className="flex h-8 w-8 items-center justify-center text-[#5A6B5A]" disabled>
            <i className="ri-subtract-line text-xs" />
          </button>
          <span className="w-6 text-center text-xs font-bold text-[#1B3D2A]">1</span>
          <button type="button" className="flex h-8 w-8 items-center justify-center text-[#5A6B5A]" disabled>
            <i className="ri-add-line text-xs" />
          </button>
        </div>
      </div>
      <button
        type="button"
        disabled
        title="預覽模式僅供檢視版面，正式頁面才可實際加入購物車"
        className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3 text-sm font-bold text-white opacity-60"
      >
        <i className="ri-shopping-cart-2-line" />
        加入購物車（預覽）
      </button>
    </div>
  );
}

function ProductPreviewModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const quickInfoItems = (product.quickInfoItems || []).filter((item) => item.Label?.trim() || item.Value?.trim());
  const displayPrice = product.salePrice || product.price;
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const activeVariants = product.variants.filter((variant) => variant.isActive);
  const selectedVariant = product.hasVariants ? (activeVariants[selectedVariantIndex] || activeVariants[0] || null) : null;

  return (
    <div className="fixed inset-0 z-50 bg-[#0f1f18]/70 p-3 md:p-6">
      <div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-[#dfe6e1] bg-white px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">產品預覽</div>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{product.name || "未命名產品"}</h2>
          </div>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={onClose}>
            <X size={18} />
            關閉預覽
          </button>
        </div>
        <div className="overflow-auto">
          <main className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-8 md:px-10 md:py-12 lg:px-16">
              <div className="flex flex-col gap-8 md:gap-10 lg:flex-row lg:gap-16">
                <div className="lg:w-1/2">
                  <ProductGallery
                    key={selectedVariant?.id ?? "base"}
                    productName={product.name || "產品圖片"}
                    image={selectedVariant?.imageUrl || product.image}
                    galleryImages={product.galleryImages}
                  />
                </div>
                <div className="flex flex-col lg:w-1/2">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#D4A574]">{product.subtitle}</div>
                  <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">{product.name || "未命名產品"}</h1>
                  {product.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.tags.map((tag) => (
                        <span className="rounded-full border border-[#1B3D2A]/10 bg-[#1B3D2A]/8 px-3 py-1.5 text-sm font-medium text-[#1B3D2A]" key={tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-6 flex flex-wrap items-baseline gap-2">
                    <span className="text-sm text-[#9A8A7A]">NT$</span>
                    <span className="text-3xl font-bold text-[#1B3D2A] md:text-4xl">{displayPrice.toLocaleString()}</span>
                    {product.salePrice && <span className="text-base font-semibold text-[#9A8A7A] line-through">NT$ {product.price.toLocaleString()}</span>}
                  </div>
                  {product.shortDescription && <p className="mt-4 text-base leading-relaxed text-[#5A6B5A]">{product.shortDescription}</p>}
                  {quickInfoItems.length > 0 && (
                    <div className="mt-6 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-5 md:p-6">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {quickInfoItems.map((item) => (
                          <div className="flex items-center gap-3" key={`${item.Label}-${item.Value}`}>
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1B3D2A]/10">
                              <i className={`${item.Icon || "ri-information-line"} text-sm text-[#1B3D2A]`} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs text-[#9A8A7A]">{item.Label}</div>
                              <div className="break-words text-sm font-semibold text-[#1B3D2A]">{item.Value}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <PreviewCartSection
                    product={product}
                    activeVariants={activeVariants}
                    selectedVariant={selectedVariant}
                    onSelectVariantIndex={setSelectedVariantIndex}
                  />
                </div>
              </div>
              <div className="mt-10 grid gap-8">
                <ProductFeatures tags={product.tags} features={product.featureItems || []} />
                <ProductSpecs specs={product.specItems || []} />
                <ProductDescriptionPreview description={product.description} />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default function AdminProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const parsedProductId = id ? Number(id) : null;
  const productId = parsedProductId && Number.isInteger(parsedProductId) && parsedProductId > 0 ? parsedProductId : null;
  const isEditing = Boolean(productId);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [message, setMessage] = useState("");
  const [initialImageUrls, setInitialImageUrls] = useState<string[]>([]);
  const [pendingDeletedImageUrls, setPendingDeletedImageUrls] = useState<string[]>([]);
  const [draggingImageIndex, setDraggingImageIndex] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const parentCategories = useMemo(
    () => categories.filter((item) => item.isActive && !item.parentCategoryId),
    [categories],
  );
  const childCategories = useMemo(
    () => categories.filter((item) => item.isActive && item.parentCategoryId === Number(form.ParentCategoryId)),
    [categories, form.ParentCategoryId],
  );
  const selectedCategoryName = useMemo(() => {
    const child = categories.find((item) => item.id === Number(form.ChildCategoryId));
    const parent = categories.find((item) => item.id === Number(form.ParentCategoryId));
    return child?.name || parent?.name || "";
  }, [categories, form.ChildCategoryId, form.ParentCategoryId]);
  const selectedCategoryId = useMemo(() => {
    return Number(form.ChildCategoryId || form.ParentCategoryId) || null;
  }, [form.ChildCategoryId, form.ParentCategoryId]);

  const [uploadingVariantIndex, setUploadingVariantIndex] = useState<number | null>(null);
  const [newAttributeValueDrafts, setNewAttributeValueDrafts] = useState<Record<number, string>>({});

  function addVariantAttribute() {
    setForm((current) => ({ ...current, VariantAttributes: [...current.VariantAttributes, { Name: "", Values: [] }] }));
  }

  function removeVariantAttribute(index: number) {
    setForm((current) => ({ ...current, VariantAttributes: current.VariantAttributes.filter((_, itemIndex) => itemIndex !== index) }));
  }

  function updateVariantAttributeName(index: number, name: string) {
    setForm((current) => ({
      ...current,
      VariantAttributes: current.VariantAttributes.map((attribute, itemIndex) => (itemIndex === index ? { ...attribute, Name: name } : attribute)),
    }));
  }

  function addVariantAttributeValue(index: number) {
    const draft = (newAttributeValueDrafts[index] || "").trim();
    if (!draft) return;
    setForm((current) => ({
      ...current,
      VariantAttributes: current.VariantAttributes.map((attribute, itemIndex) => {
        if (itemIndex !== index) return attribute;
        if (attribute.Values.includes(draft)) return attribute;
        return { ...attribute, Values: [...attribute.Values, draft] };
      }),
    }));
    setNewAttributeValueDrafts((current) => ({ ...current, [index]: "" }));
  }

  function removeVariantAttributeValue(attributeIndex: number, valueIndex: number) {
    setForm((current) => ({
      ...current,
      VariantAttributes: current.VariantAttributes.map((attribute, itemIndex) =>
        itemIndex === attributeIndex ? { ...attribute, Values: attribute.Values.filter((_, vIndex) => vIndex !== valueIndex) } : attribute
      ),
    }));
  }

  /** 依目前的規格屬性重新產生組合矩陣：保留已存在組合的資料（價格/庫存/圖片等），新增缺少的組合、移除已不存在的組合。 */
  function regenerateVariants() {
    setForm((current) => {
      const sanitizedAttributes = current.VariantAttributes
        .map((attribute) => ({ Name: attribute.Name.trim(), Values: Array.from(new Set(attribute.Values.map((v) => v.trim()).filter(Boolean))) }))
        .filter((attribute) => attribute.Name !== "" && attribute.Values.length > 0);
      const attributeNames = sanitizedAttributes.map((attribute) => attribute.Name);
      const combinations = cartesianCombinations(sanitizedAttributes);

      const existingByKey = new Map(current.Variants.map((row) => [comboKey(row.OptionValues, attributeNames), row]));
      const nextVariants = combinations.map((optionValues) => {
        const key = comboKey(optionValues, attributeNames);
        const existing = existingByKey.get(key);
        if (existing) return { ...existing, OptionValues: optionValues };
        return emptyVariantRow(optionValues, { Price: current.Price, SalePrice: current.SalePrice });
      });
      if (nextVariants.length > 0 && !nextVariants.some((row) => row.IsDefault)) {
        nextVariants[0] = { ...nextVariants[0], IsDefault: true };
      }

      return { ...current, VariantAttributes: sanitizedAttributes, Variants: nextVariants };
    });
  }

  function updateVariantRow(index: number, patch: Partial<VariantRowForm>) {
    setForm((current) => ({
      ...current,
      Variants: current.Variants.map((row, itemIndex) => (itemIndex === index ? { ...row, ...patch } : row)),
    }));
  }

  function setDefaultVariant(index: number) {
    setForm((current) => ({
      ...current,
      Variants: current.Variants.map((row, itemIndex) => ({ ...row, IsDefault: itemIndex === index })),
    }));
  }

  function moveVariant(index: number, direction: -1 | 1) {
    setForm((current) => ({ ...current, Variants: moveVariantList(current.Variants, index, direction) }));
  }

  function removeVariantRow(index: number) {
    setForm((current) => {
      const nextVariants = current.Variants.filter((_, itemIndex) => itemIndex !== index);
      if (nextVariants.length > 0 && !nextVariants.some((row) => row.IsDefault)) {
        nextVariants[0] = { ...nextVariants[0], IsDefault: true };
      }
      return { ...current, Variants: nextVariants };
    });
  }

  async function handleVariantImageUpload(index: number, file: File) {
    setUploadingVariantIndex(index);
    setMessage("");
    try {
      const url = await uploadAdminProductImage(file);
      updateVariantRow(index, { ImageUrl: url });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "規格圖片上傳失敗。");
    } finally {
      setUploadingVariantIndex(null);
    }
  }

  function resolveCategorySelection(categoryName: string, rows: ProductCategory[], categoryId?: number | null) {
    const activeRows = rows.filter((item) => item.isActive);
    const matchedById = activeRows.find((item) => item.id === categoryId);
    if (matchedById?.parentCategoryId) {
      return {
        ParentCategoryId: String(matchedById.parentCategoryId),
        ChildCategoryId: String(matchedById.id),
        Category: matchedById.name,
      };
    }
    if (matchedById) {
      const firstChild = activeRows.find((item) => item.parentCategoryId === matchedById.id);
      return {
        ParentCategoryId: String(matchedById.id),
        ChildCategoryId: firstChild ? String(firstChild.id) : "",
        Category: firstChild?.name || matchedById.name,
      };
    }
    const matchedCategory = activeRows.find((item) => item.name === categoryName);
    if (matchedCategory?.parentCategoryId) {
      return {
        ParentCategoryId: String(matchedCategory.parentCategoryId),
        ChildCategoryId: String(matchedCategory.id),
        Category: matchedCategory.name,
      };
    }
    if (matchedCategory) {
      const firstChild = activeRows.find((item) => item.parentCategoryId === matchedCategory.id);
      return {
        ParentCategoryId: String(matchedCategory.id),
        ChildCategoryId: firstChild ? String(firstChild.id) : "",
        Category: firstChild?.name || matchedCategory.name,
      };
    }
    const firstParent = activeRows.find((item) => !item.parentCategoryId);
    const firstChild = firstParent ? activeRows.find((item) => item.parentCategoryId === firstParent.id) : null;
    return {
      ParentCategoryId: firstParent ? String(firstParent.id) : "",
      ChildCategoryId: firstChild ? String(firstChild.id) : "",
      Category: firstChild?.name || firstParent?.name || "",
    };
  }

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setMessage("");
      try {
        const me = await getCurrentAdmin();
        const categoryRows = await listAdminProductCategories();
        let nextForm = emptyForm;
        if (productId) {
          const product = await getAdminProduct(productId);
          nextForm = productToForm(product);
          const categorySelection = resolveCategorySelection(nextForm.Category, categoryRows, product.productCategoryId);
          if (!alive) return;
          setCurrentUser(me.User);
          setCategories(categoryRows);
          setForm({ ...nextForm, ...categorySelection });
          setInitialImageUrls(nextForm.ProductImageUrls);
          setPendingDeletedImageUrls([]);
          return;
        }
        const categorySelection = resolveCategorySelection(nextForm.Category, categoryRows);
        if (!alive) return;
        setCurrentUser(me.User);
        setCategories(categoryRows);
        setForm({ ...nextForm, ...categorySelection });
        setInitialImageUrls(nextForm.ProductImageUrls);
        setPendingDeletedImageUrls([]);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "產品資料載入失敗。");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [productId]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function validateForm() {
    if (!form.Name.trim()) return "請輸入產品名稱。";
    if (!form.Slug.trim()) return "請輸入網址代稱。";
    if (!form.Subtitle.trim()) return "請輸入產品副標。";
    if (!selectedCategoryName.trim()) return "請選擇產品類別。";
    if (!Number.isFinite(Number(form.Price)) || Number(form.Price) < 0) return "請輸入正確價格。";
    if (form.SalePrice.trim() && (!Number.isFinite(Number(form.SalePrice)) || Number(form.SalePrice) < 0)) return "請輸入正確優惠價格。";
    if (form.SalePrice.trim() && Number(form.SalePrice) >= Number(form.Price)) return "優惠價格需小於原價。";
    if (!form.ShortDescription.trim()) return "請輸入簡短描述。";
    if (form.ProductImageUrls.length === 0) return "請至少上傳一張商品圖。";
    if (!form.Description.trim()) return "請輸入產品描述。";
    if (!form.HasVariants && form.Stock.trim() && (!Number.isFinite(Number(form.Stock)) || Number(form.Stock) < 0)) return "請輸入正確庫存數量。";
    if (form.HasVariants) {
      const validAttributes = form.VariantAttributes.filter((attribute) => attribute.Name.trim() && attribute.Values.length > 0);
      if (validAttributes.length === 0) return "請至少設定一組規格屬性與選項（例如：顏色）。";
      if (form.Variants.length === 0) return "請點擊「產生規格組合」建立規格組合矩陣。";
      const activeVariants = form.Variants.filter((row) => row.IsActive);
      if (activeVariants.length === 0) return "請至少啟用一組規格組合。";
      for (const row of activeVariants) {
        if (!Number.isFinite(Number(row.Price)) || Number(row.Price) < 0) return `規格「${buildOptionLabel(row.OptionValues, form.VariantAttributes)}」的價格不正確。`;
        if (row.SalePrice.trim() && (!Number.isFinite(Number(row.SalePrice)) || Number(row.SalePrice) < 0)) return `規格「${buildOptionLabel(row.OptionValues, form.VariantAttributes)}」的優惠價格不正確。`;
        if (row.SalePrice.trim() && Number(row.SalePrice) >= Number(row.Price)) return `規格「${buildOptionLabel(row.OptionValues, form.VariantAttributes)}」的優惠價格需小於原價。`;
        if (!Number.isFinite(Number(row.Stock)) || Number(row.Stock) < 0) return `規格「${buildOptionLabel(row.OptionValues, form.VariantAttributes)}」的庫存數量不正確。`;
      }
    }
    return "";
  }

  async function handleUpload(files: FileList) {
    const imageFiles = Array.from(files);
    if (imageFiles.length === 0) return;
    setUploading(true);
    setMessage("");
    try {
      const uploadedUrls = await Promise.all(imageFiles.map((file) => uploadAdminProductImage(file)));
      setForm((current) => {
        return { ...current, ProductImageUrls: uniqueUrls([...current.ProductImageUrls, ...uploadedUrls]) };
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "圖片上傳失敗。");
    } finally {
      setUploading(false);
    }
  }

  async function handleEditorImageUpload(file: File) {
    setMessage("");
    try {
      return await uploadAdminProductImage(file);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "描述圖片上傳失敗。");
      throw error;
    }
  }

  function moveImage(index: number, direction: -1 | 1) {
    setForm((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.ProductImageUrls.length) return current;
      const nextUrls = [...current.ProductImageUrls];
      [nextUrls[index], nextUrls[nextIndex]] = [nextUrls[nextIndex], nextUrls[index]];
      return { ...current, ProductImageUrls: nextUrls };
    });
  }

  function reorderImage(fromIndex: number, toIndex: number) {
    setForm((current) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= current.ProductImageUrls.length || toIndex >= current.ProductImageUrls.length) {
        return current;
      }
      const nextUrls = [...current.ProductImageUrls];
      const [movedUrl] = nextUrls.splice(fromIndex, 1);
      nextUrls.splice(toIndex, 0, movedUrl);
      return { ...current, ProductImageUrls: nextUrls };
    });
  }

  async function removeImage(index: number) {
    const url = form.ProductImageUrls[index];
    if (!url) return;

    if (initialImageUrls.includes(url)) {
      setForm((current) => ({
        ...current,
        ProductImageUrls: current.ProductImageUrls.filter((_, itemIndex) => itemIndex !== index),
      }));
      setPendingDeletedImageUrls((current) => uniqueUrls([...current, url]));
      return;
    }

    setDeletingImage(true);
    setMessage("");
    try {
      await deleteAdminProductImage(url);
      setForm((current) => ({
        ...current,
        ProductImageUrls: current.ProductImageUrls.filter((itemUrl) => itemUrl !== url),
      }));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "圖片實體檔刪除失敗。");
    } finally {
      setDeletingImage(false);
    }
  }

  function handleParentCategoryChange(parentCategoryId: string) {
    const firstChild = categories.find((item) => item.isActive && item.parentCategoryId === Number(parentCategoryId));
    const parent = categories.find((item) => item.id === Number(parentCategoryId));
    setForm((current) => ({
      ...current,
      ParentCategoryId: parentCategoryId,
      ChildCategoryId: firstChild ? String(firstChild.id) : "",
      Category: firstChild?.name || parent?.name || "",
    }));
  }

  function handleChildCategoryChange(childCategoryId: string) {
    const child = categories.find((item) => item.id === Number(childCategoryId));
    const parent = categories.find((item) => item.id === Number(form.ParentCategoryId));
    setForm((current) => ({
      ...current,
      ChildCategoryId: childCategoryId,
      Category: child?.name || parent?.name || "",
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationMessage = validateForm();
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    const validAttributes = form.VariantAttributes.filter((attribute) => attribute.Name.trim() && attribute.Values.length > 0);

    const payload: ProductPayload = {
      Name: form.Name.trim(),
      Slug: slugify(form.Slug),
      Subtitle: form.Subtitle.trim(),
      ProductCode: form.ProductCode.trim(),
      Barcode: form.Barcode.trim(),
      ProductCategoryId: selectedCategoryId,
      ProductType: form.ProductType,
      ProductUsageType: form.ProductUsageType,
      AllowZeroPriceOrder: form.AllowZeroPriceOrder,
      HasVariants: form.HasVariants,
      Price: Number(form.Price),
      SalePrice: form.SalePrice.trim() ? Number(form.SalePrice) : null,
      Stock: !form.HasVariants && form.Stock.trim() ? Number(form.Stock) : null,
      Tags: splitLines(form.TagsText),
      ShortDescription: form.ShortDescription.trim(),
      MainImageUrl: form.ProductImageUrls[0],
      GalleryImageUrls: form.ProductImageUrls.slice(1),
      Description: form.Description.trim(),
      FeatureItems: parseFeatureItems(form.FeatureItemsText),
      SpecItems: parseSpecItems(form.SpecItemsText),
      QuickInfoItems: parseQuickInfoItems(form.QuickInfoItemsText),
      VariantAttributes: form.HasVariants ? validAttributes.map((attribute) => ({ Name: attribute.Name.trim(), Values: attribute.Values })) : [],
      Variants: form.HasVariants
        ? form.Variants.map((row, index) => ({
            Sku: row.Sku.trim(),
            OptionValues: row.OptionValues,
            Price: Number(row.Price || 0),
            SalePrice: row.SalePrice.trim() ? Number(row.SalePrice) : null,
            Stock: Number(row.Stock || 0),
            ImageUrl: row.ImageUrl.trim() || null,
            IsDefault: row.IsDefault,
            IsActive: row.IsActive,
            SortOrder: (index + 1) * 100,
          }))
        : [],
      IsFeatured: form.IsFeatured,
      IsNew: form.IsNew,
      IsHot: form.IsHot,
      IsPublished: form.IsPublished,
      SortOrder: Number(form.SortOrder || 0),
    };

    setSaving(true);
    setMessage("");
    try {
      await saveAdminProduct(productId, payload);
      await Promise.all(pendingDeletedImageUrls.map((url) => deleteAdminProductImage(url).catch(() => undefined)));
      navigate("/admin/products");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "產品儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  function previewProduct(): Product {
    const validAttributes = form.VariantAttributes.filter((attribute) => attribute.Name.trim() && attribute.Values.length > 0);
    return {
      id: productId || 0,
      name: form.Name.trim() || "未命名產品",
      slug: slugify(form.Slug) || String(productId || 0),
      subtitle: form.Subtitle.trim(),
      productCode: form.ProductCode.trim(),
      barcode: form.Barcode.trim() || null,
      productCategoryId: selectedCategoryId,
      productType: form.ProductType,
      productUsageType: form.ProductUsageType,
      allowZeroPriceOrder: form.AllowZeroPriceOrder,
      hasVariants: form.HasVariants,
      price: Number(form.Price || 0),
      salePrice: form.SalePrice.trim() ? Number(form.SalePrice) : null,
      stock: !form.HasVariants && form.Stock.trim() ? Number(form.Stock) : null,
      category: selectedCategoryName,
      tags: splitLines(form.TagsText),
      shortDescription: form.ShortDescription.trim(),
      image: form.ProductImageUrls[0] || "",
      galleryImages: form.ProductImageUrls.slice(1),
      description: sanitizeEditorHtml(form.Description.trim()),
      featureItems: parseFeatureItems(form.FeatureItemsText),
      specItems: parseSpecItems(form.SpecItemsText),
      quickInfoItems: parseQuickInfoItems(form.QuickInfoItemsText),
      variantAttributes: validAttributes.map((attribute) => ({ name: attribute.Name.trim(), values: attribute.Values })),
      variants: form.HasVariants
        ? form.Variants.map((row, index) => ({
            id: index,
            sku: row.Sku.trim(),
            optionValues: row.OptionValues,
            optionLabel: buildOptionLabel(row.OptionValues, form.VariantAttributes),
            price: Number(row.Price || 0),
            salePrice: row.SalePrice.trim() ? Number(row.SalePrice) : null,
            stock: Number(row.Stock || 0),
            imageUrl: row.ImageUrl.trim() || null,
            isDefault: row.IsDefault,
            isActive: row.IsActive,
            sortOrder: (index + 1) * 100,
          }))
        : [],
      variantCount: form.HasVariants ? form.Variants.length : 0,
      stockStatus: "ok",
      isFeatured: form.IsFeatured,
      isNew: form.IsNew,
      isHot: form.IsHot,
      isPublished: form.IsPublished,
      sortOrder: Number(form.SortOrder || 0),
      createdAt: null,
      updatedAt: null,
    };
  }

  return (
    <AdminLayout sectionLabel="產品管理" title={isEditing ? "編輯產品" : "新增產品"} user={currentUser} onLogout={handleLogout}>
      <form className="grid gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:sticky md:top-[102px] md:z-10 md:flex-row md:items-center md:justify-between md:bg-white/95 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur">
          <button className="inline-flex h-10 w-fit items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => navigate("/admin/products")}>
            <ArrowLeft size={18} />
            返回列表
          </button>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} type="button" onClick={() => setPreviewOpen(true)}>
              <Eye size={18} />
              預覽
            </button>
            <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving || uploading || deletingImage || loading} type="submit">
              <Save size={18} />
              {saving ? "儲存中" : "儲存產品"}
            </button>
          </div>
        </div>

        <AdminFormNotice message={message} />
        {loading ? <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div> : null}

        {!loading && (
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="space-y-5">
            <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4 lg:grid-cols-12">
              <h2 className="text-base font-bold text-[#315746] lg:col-span-12">基本資訊</h2>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-6"><span>產品名稱<RequiredMark /></span><input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={120} value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value, Slug: current.Slug || slugify(event.target.value) }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-6"><span>網址代稱 Slug<RequiredMark /></span><input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={120} placeholder="hearing-pro-3" value={form.Slug} onChange={(event) => setForm((current) => ({ ...current, Slug: slugify(event.target.value) }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-12"><span>副標<RequiredMark /></span><input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={160} value={form.Subtitle} onChange={(event) => setForm((current) => ({ ...current, Subtitle: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-6">產品編號<input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={40} placeholder="留空由系統產生" value={form.ProductCode} onChange={(event) => setForm((current) => ({ ...current, ProductCode: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-6">條碼編號<input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={80} value={form.Barcode} onChange={(event) => setForm((current) => ({ ...current, Barcode: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746] lg:col-span-12"><span>簡短描述<RequiredMark /></span><textarea className="min-h-[82px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={300} placeholder="顯示於產品列表與明細上方，建議 80-120 字" value={form.ShortDescription} onChange={(event) => setForm((current) => ({ ...current, ShortDescription: event.target.value }))} /></label>
            </section>

            <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="flex items-center gap-2">
                <Boxes className="text-[#315746]" size={18} />
                <div>
                  <h2 className="text-base font-bold text-[#315746]">規格與庫存設定</h2>
                  <p className="mt-1 text-sm text-[#7b877f]">單一規格商品可直接設定庫存；多規格商品可依顏色、耳型等屬性建立組合矩陣，各組合可分別設定價格、庫存、SKU 與圖片。</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]"><span>價格<RequiredMark /></span><input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" min={0} type="number" value={form.Price} onChange={(event) => setForm((current) => ({ ...current, Price: event.target.value }))} /></label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">優惠價格<input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" min={0} placeholder="無優惠可留空" type="number" value={form.SalePrice} onChange={(event) => setForm((current) => ({ ...current, SalePrice: event.target.value }))} /></label>
              </div>

              <AdminSwitchCard
                checked={form.HasVariants}
                label="此商品為多規格商品"
                description={form.HasVariants ? "已啟用多規格，請在下方設定規格屬性並產生組合矩陣。" : "單一規格商品，使用下方庫存欄位即可（留空表示不限量）。"}
                onChange={(HasVariants) => setForm((current) => ({ ...current, HasVariants }))}
              />

              {!form.HasVariants && (
                <label className="grid max-w-xs gap-2 text-sm font-bold text-[#315746]">
                  庫存數量
                  <input
                    className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 disabled:bg-[#f6f7f5] disabled:text-[#8b9690]"
                    min={0}
                    placeholder="留空表示不追蹤庫存（不限量）"
                    type="number"
                    value={form.Stock}
                    disabled={form.ProductType === "Display"}
                    onChange={(event) => setForm((current) => ({ ...current, Stock: event.target.value }))}
                  />
                  {form.ProductType === "Display" && <span className="text-xs font-normal text-[#9a8a7a]">展示型商品不需設定庫存。</span>}
                </label>
              )}

              {form.HasVariants && (
                <div className="grid gap-4">
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#315746]">規格屬性<RequiredMark /></h3>
                      <button
                        type="button"
                        onClick={addVariantAttribute}
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]"
                      >
                        <Plus size={14} />
                        新增屬性
                      </button>
                    </div>

                    <div className="flex items-start gap-2.5 rounded-md border border-[#cfe0d5] bg-[#f1f8f3] px-3.5 py-3 text-xs leading-5 text-[#2f5744]">
                      <Info className="mt-0.5 shrink-0" size={15} />
                      <p>
                        一個「屬性」代表一種規格維度，「選項」是這個維度底下的多個選擇。例如屬性設為<strong className="font-bold">顏色</strong>，選項可新增<strong className="font-bold">咖啡色、灰色、黑色</strong>三個；
                        <strong className="font-bold">請勿</strong>把顏色名稱各自設成一個屬性。庫存、價格、SKU 請在下方「產生規格組合矩陣」後，於每一組規格各自填寫，不是在這裡輸入。
                      </p>
                    </div>

                    {form.VariantAttributes.length === 0 && (
                      <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f6f7f5] px-4 py-6 text-center text-sm text-[#7b877f]">
                        尚未設定規格屬性，例如屬性「顏色」搭配選項「咖啡色」、「灰色」、「黑色」
                      </div>
                    )}

                    {form.VariantAttributes.map((attribute, index) => (
                      <div className="grid gap-2 rounded-md border border-[#dfe6e1] p-3" key={index}>
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 text-xs font-bold text-[#9aa59f]">屬性</span>
                          <input
                            className="h-10 flex-1 rounded-md border border-[#cfd8d2] px-3 text-sm font-bold text-[#315746] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                            placeholder="屬性名稱，例如：顏色、耳型"
                            value={attribute.Name}
                            onChange={(event) => updateVariantAttributeName(index, event.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => removeVariantAttribute(index)}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#f0c7c4] bg-white text-[#9a2d2d] hover:bg-[#fdebea]"
                            aria-label="移除此屬性"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pl-[46px]">
                          <span className="shrink-0 text-xs font-bold text-[#9aa59f]">選項</span>
                          {attribute.Values.map((value, valueIndex) => (
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full bg-[#e9f4ec] px-3 py-1 text-xs font-bold text-[#245438]"
                              key={value}
                            >
                              {value}
                              <button type="button" onClick={() => removeVariantAttributeValue(index, valueIndex)} aria-label={`移除選項 ${value}`}>
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                          <input
                            className="h-8 w-40 rounded-full border border-[#cfd8d2] px-3 text-xs font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                            placeholder="輸入選項後按 Enter，例如：咖啡色"
                            value={newAttributeValueDrafts[index] || ""}
                            onChange={(event) => setNewAttributeValueDrafts((current) => ({ ...current, [index]: event.target.value }))}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                addVariantAttributeValue(index);
                              }
                            }}
                          />
                        </div>
                        {attribute.Name.trim() && attribute.Values.length === 1 && (
                          <p className="pl-[46px] text-xs font-medium text-[#b5791f]">
                            這個屬性只有 1 個選項。如果「{attribute.Values[0]}」其實是庫存數量，請刪除它，並改在下方「產生規格組合矩陣」後的規格列裡填寫庫存。
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={regenerateVariants}
                      className="inline-flex h-10 w-fit items-center gap-2 rounded-md bg-[#315746] px-4 text-sm font-bold text-white hover:bg-[#203d32]"
                    >
                      <Sparkles size={16} />
                      產生 / 更新規格組合矩陣
                    </button>
                    <p className="text-xs leading-5 text-[#9aa59f]">
                      可用每組右上角的刪除鍵移除單一規格組合；若上方屬性與選項仍涵蓋該組合，再次按「產生 / 更新」時可能會重新加入。
                    </p>
                  </div>

                  {form.Variants.length > 0 && (
                    <div className="grid gap-3">
                      <h3 className="text-sm font-bold text-[#315746]">規格組合（共 {form.Variants.length} 組，啟用 {form.Variants.filter((row) => row.IsActive).length} 組）</h3>
                      <div className="grid gap-3">
                        {form.Variants.map((row, index) => (
                          <div
                            className={`rounded-md border ${row.IsActive ? "border-[#dfe6e1] bg-white" : "border-[#e6dcdc] bg-[#faf7f7]"}`}
                            key={index}
                          >
                            {/* 標頭：規格組合標籤 + 排序 + 預設 / 啟用 */}
                            <div className="flex flex-col gap-3 border-b border-[#eef1ef] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {form.VariantAttributes.map((attribute) =>
                                  row.OptionValues[attribute.Name] ? (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]"
                                      key={attribute.Name}
                                    >
                                      <span className="font-normal text-[#5f8a72]">{attribute.Name}</span>
                                      {row.OptionValues[attribute.Name]}
                                    </span>
                                  ) : null
                                )}
                                {form.VariantAttributes.length === 0 && <span className="text-sm text-[#9aa59f]">—</span>}
                              </div>
                              <div className="flex flex-wrap items-center gap-2.5 sm:justify-end">
                                <div className="flex items-center gap-0.5 rounded-md border border-[#e3e8e5] p-0.5">
                                  <button
                                    type="button"
                                    className="flex h-7 w-7 items-center justify-center rounded text-[#59665f] hover:bg-[#f0f5f2] disabled:opacity-30"
                                    disabled={index === 0}
                                    title="往前排序"
                                    onClick={() => moveVariant(index, -1)}
                                  >
                                    <ArrowUp size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="flex h-7 w-7 items-center justify-center rounded text-[#59665f] hover:bg-[#f0f5f2] disabled:opacity-30"
                                    disabled={index === form.Variants.length - 1}
                                    title="往後排序"
                                    onClick={() => moveVariant(index, 1)}
                                  >
                                    <ArrowDown size={14} />
                                  </button>
                                </div>
                                <VariantDefaultButton checked={row.IsDefault} onClick={() => setDefaultVariant(index)} />
                                <VariantActiveSwitch checked={row.IsActive} onChange={(IsActive) => updateVariantRow(index, { IsActive })} />
                                <button
                                  type="button"
                                  onClick={() => removeVariantRow(index)}
                                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#f0c7c4] bg-white text-[#9a2d2d] hover:bg-[#fdebea]"
                                  title="刪除此規格組合"
                                  aria-label="刪除此規格組合"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>

                            {/* 內容：圖片 + 各欄位（分兩排，避免欄位擠在同一行） */}
                            <div className="flex flex-col gap-4 p-4 sm:flex-row">
                              <div className="flex shrink-0 flex-row items-center gap-3 sm:flex-col sm:items-start">
                                {row.ImageUrl ? (
                                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border border-[#dfe6e1]">
                                    <img src={row.ImageUrl} alt="規格圖片" className="h-full w-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => updateVariantRow(index, { ImageUrl: "" })}
                                      className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center bg-black/50 text-white"
                                      aria-label="移除規格圖片"
                                    >
                                      <X size={11} />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-[#cfd8d2] text-[#9aa59f] hover:border-[#315746] hover:text-[#315746]">
                                    <ImagePlus size={20} />
                                    <span className="text-[10px] font-bold">上傳圖片</span>
                                    <input
                                      accept="image/jpeg,image/png,image/webp,image/gif"
                                      className="hidden"
                                      type="file"
                                      onChange={(event) => event.target.files?.[0] && handleVariantImageUpload(index, event.target.files[0])}
                                    />
                                  </label>
                                )}
                                {uploadingVariantIndex === index && <span className="text-xs text-[#7b877f]">上傳中...</span>}
                              </div>

                              <div className="grid flex-1 gap-3">
                                <label className="grid gap-1.5 text-xs font-bold text-[#315746]">
                                  SKU
                                  <input
                                    className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                                    placeholder="留空自動產生"
                                    value={row.Sku}
                                    onChange={(event) => updateVariantRow(index, { Sku: event.target.value })}
                                  />
                                </label>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                  <label className="grid gap-1.5 text-xs font-bold text-[#315746]">
                                    價格
                                    <input
                                      className="h-11 rounded-md border border-[#cfd8d2] px-3 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                                      min={0}
                                      type="number"
                                      value={row.Price}
                                      onChange={(event) => updateVariantRow(index, { Price: event.target.value })}
                                    />
                                  </label>
                                  <label className="grid gap-1.5 text-xs font-bold text-[#315746]">
                                    優惠價
                                    <input
                                      className="h-11 rounded-md border border-[#cfd8d2] px-3 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                                      min={0}
                                      placeholder="無優惠可留空"
                                      type="number"
                                      value={row.SalePrice}
                                      onChange={(event) => updateVariantRow(index, { SalePrice: event.target.value })}
                                    />
                                  </label>
                                  <label className="grid gap-1.5 text-xs font-bold text-[#315746]">
                                    庫存
                                    <input
                                      className="h-11 rounded-md border border-[#cfd8d2] px-3 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                                      min={0}
                                      type="number"
                                      value={row.Stock}
                                      onChange={(event) => updateVariantRow(index, { Stock: event.target.value })}
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-base font-bold text-[#315746]">商品圖片<RequiredMark /></h2>
                  <p className="mt-1 text-sm text-[#7b877f]">可一次上傳多張，排序第 1 張會自動作為產品主圖。</p>
                </div>
                <label className="inline-flex h-10 w-fit cursor-pointer items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]">
                  <ImagePlus size={16} />上傳商品圖
                  <input accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" multiple type="file" onChange={(event) => event.target.files && handleUpload(event.target.files)} />
                </label>
              </div>

              {uploading && <p className="text-sm text-[#7b877f]">圖片上傳中...</p>}
              {deletingImage && <p className="text-sm text-[#7b877f]">圖片刪除中...</p>}

              {form.ProductImageUrls.length === 0 ? (
                <div className="flex min-h-[180px] items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-[#f6f7f5] px-4 text-center text-sm font-bold text-[#7b877f]">
                  尚未上傳商品圖片
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {form.ProductImageUrls.map((url, index) => (
                    <div
                      className={`grid gap-3 rounded-md border bg-[#fbfcfb] p-3 transition ${draggingImageIndex === index ? "border-[#315746] opacity-70" : "border-[#dfe6e1]"}`}
                      draggable
                      key={url}
                      onDragEnd={() => setDraggingImageIndex(null)}
                      onDragOver={(event) => event.preventDefault()}
                      onDragStart={() => setDraggingImageIndex(index)}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (draggingImageIndex !== null) reorderImage(draggingImageIndex, index);
                        setDraggingImageIndex(null);
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-bold ${index === 0 ? "bg-[#315746] text-white" : "bg-[#e9f4ec] text-[#315746]"}`}>
                          {index === 0 ? "主圖" : `圖 ${index + 1}`}
                        </span>
                        <GripVertical className="text-[#9aa59f]" size={18} />
                      </div>
                      <div className="aspect-square overflow-hidden rounded-md border border-[#dfe6e1] bg-white">
                        <img className="h-full w-full object-cover" src={url} alt={`商品圖片 ${index + 1}`} />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <button className="inline-flex h-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-40" disabled={index === 0} title="往前排序" type="button" onClick={() => moveImage(index, -1)}>
                          <ArrowUp size={16} />
                        </button>
                        <button className="inline-flex h-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-40" disabled={index === form.ProductImageUrls.length - 1} title="往後排序" type="button" onClick={() => moveImage(index, 1)}>
                          <ArrowDown size={16} />
                        </button>
                        <button className="inline-flex h-9 items-center justify-center rounded-md border border-[#f0c7c4] bg-white text-[#9a2d2d] hover:bg-[#fdebea] disabled:opacity-40" disabled={deletingImage} title="移除圖片" type="button" onClick={() => removeImage(index)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4 lg:grid-cols-3">
              <label className="grid gap-2 text-sm font-bold text-[#315746] md:col-span-2">
                明細重點資訊卡
                <textarea
                  className="min-h-[132px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  placeholder={"每行一筆：圖示｜標題｜內容\n例如：ri-shield-check-line｜保固期｜2 年原廠保固"}
                  value={form.QuickInfoItemsText}
                  onChange={(event) => setForm((current) => ({ ...current, QuickInfoItemsText: event.target.value }))}
                />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                產品功能特色
                <textarea
                  className="min-h-[132px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  placeholder={"每行一筆：標題｜說明\n例如：AI 智能降噪｜自動識別環境噪音，讓對話更清晰自然"}
                  value={form.FeatureItemsText}
                  onChange={(event) => setForm((current) => ({ ...current, FeatureItemsText: event.target.value }))}
                />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                產品規格
                <textarea
                  className="min-h-[132px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  placeholder={"每行一筆：項目｜內容\n例如：保固期｜2 年原廠保固"}
                  value={form.SpecItemsText}
                  onChange={(event) => setForm((current) => ({ ...current, SpecItemsText: event.target.value }))}
                />
              </label>
            </section>

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <label className="mb-2 block text-sm font-bold text-[#315746]">產品描述<RequiredMark /></label>
              <AdminRichTextEditor
                value={form.Description}
                onChange={(Description) => setForm((current) => ({ ...current, Description }))}
                onUploadImage={handleEditorImageUpload}
                imageAlt="產品描述圖片"
              />
            </section>
            </div>

            <aside className="space-y-5">
              <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
                <div>
                  <h2 className="text-base font-bold text-[#315746]">商品類型<RequiredMark /></h2>
                  <p className="mt-1 text-sm text-[#7b877f]">決定前台官網商品列表與商品明細頁是否顯示「加入購物車」與「選擇數量」區塊。</p>
                </div>
                <AdminRadioCardGroup
                  name="ProductType"
                  value={form.ProductType}
                  columnsClassName="grid-cols-1"
                  onChange={(ProductType) => setForm((current) => ({ ...current, ProductType }))}
                  options={[
                    {
                      value: "Sale",
                      icon: <Store size={16} />,
                      label: "銷售型",
                      description: "前台會顯示「加入購物車」按鈕與數量選擇，訪客可將商品加入購物車。",
                    },
                    {
                      value: "Display",
                      icon: <Eye size={16} />,
                      label: "展示型",
                      description: "僅用於官網展示商品資訊，前台不顯示購物車與數量選擇，適合尚未上架銷售的機型。",
                    },
                  ]}
                />
              </section>

              <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
                <div>
                  <h2 className="text-base font-bold text-[#315746]">門市用途與 0 元控管</h2>
                  <p className="mt-1 text-sm leading-relaxed text-[#7b877f]">此設定會影響門市訂單能否用 0 元成交。一般銷售商品建議不要開放，試戴、保固更換、贈品或服務補件才開啟。</p>
                </div>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  門市用途
                  <select
                    className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    value={form.ProductUsageType}
                    onChange={(event) => {
                      const ProductUsageType = event.target.value as ProductUsageType;
                      setForm((current) => ({
                        ...current,
                        ProductUsageType,
                        AllowZeroPriceOrder: defaultAllowZeroForUsage(ProductUsageType) ? true : current.AllowZeroPriceOrder,
                      }));
                    }}
                  >
                    {productUsageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <span className="text-xs font-medium leading-5 text-[#7b877f]">{productUsageOptions.find((option) => option.value === form.ProductUsageType)?.description}</span>
                </label>
                <AdminSwitchCard
                  checked={form.AllowZeroPriceOrder}
                  description="開啟後，門市建立訂單時仍需選擇 0 元原因並留下稽核資料。"
                  label="允許門市訂單 0 元成交"
                  onChange={(AllowZeroPriceOrder) => setForm((current) => ({ ...current, AllowZeroPriceOrder }))}
                />
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-base font-bold text-[#315746]">分類</h2>
                <div className="grid gap-3">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>第一層類別<RequiredMark /></span>
                    <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.ParentCategoryId} onChange={(event) => handleParentCategoryChange(event.target.value)}>
                      {parentCategories.length === 0 && <option value="">尚未建立第一層類別</option>}
                      {parentCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>第二層類別{childCategories.length > 0 && <RequiredMark />}</span>
                    <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 disabled:bg-[#f6f7f5] disabled:text-[#8b9690]" disabled={childCategories.length === 0} value={form.ChildCategoryId} onChange={(event) => handleChildCategoryChange(event.target.value)}>
                      {childCategories.length === 0 && <option value="">此第一層尚無第二層類別</option>}
                      {childCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </label>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-base font-bold text-[#315746]">標籤</h2>
                <p className="mb-3 text-sm leading-relaxed text-[#7b877f]">
                  第一個標籤會顯示在首頁精選商品圖片左上角；其他標籤會顯示於商品列表與明細頁。
                </p>
                <textarea className="min-h-[120px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="每行一個，或用逗號分隔" value={form.TagsText} onChange={(event) => setForm((current) => ({ ...current, TagsText: event.target.value }))} />
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-base font-bold text-[#315746]">發布設定</h2>
                <div className="grid gap-3">
                  <AdminSwitchCard
                    checked={form.IsPublished}
                    description={form.IsPublished ? "前台產品列表與詳情頁可看見此產品。" : "暫時從前台隱藏，後台仍可編輯。"}
                    label="前台上架"
                    onChange={(IsPublished) => setForm((current) => ({ ...current, IsPublished }))}
                  />
                  <AdminSwitchCard
                    checked={form.IsFeatured}
                    description={form.IsFeatured ? "此產品可出現在首頁精選區。" : "不納入首頁精選，但仍可在產品列表顯示。"}
                    label="首頁精選"
                    onChange={(IsFeatured) => setForm((current) => ({ ...current, IsFeatured }))}
                  />
                  <AdminSwitchCard
                    checked={form.IsNew}
                    description={form.IsNew ? "前台商品列表與明細頁會顯示新品標籤。" : "不顯示新品標籤。"}
                    label="新品標籤"
                    onChange={(IsNew) => setForm((current) => ({ ...current, IsNew }))}
                  />
                  <AdminSwitchCard
                    checked={form.IsHot}
                    description={form.IsHot ? "前台商品明細與推薦卡會顯示熱銷標籤。" : "不顯示熱銷標籤。"}
                    label="熱銷標籤"
                    onChange={(IsHot) => setForm((current) => ({ ...current, IsHot }))}
                  />
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#315746]" htmlFor="product-sort">排序</label>
                    <input id="product-sort" className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
                  </div>
                </div>
              </section>
            </aside>
          </div>
        )}
      </form>
      {previewOpen && <ProductPreviewModal product={previewProduct()} onClose={() => setPreviewOpen(false)} />}
    </AdminLayout>
  );
}

